import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MessageCard = ({ 
  message, 
  onRetry = () => {}, 
  onViewDetails = () => {},
  onContactClick = () => {},
  className = '' 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'failed':
        return 'text-error';
      default:
        return 'text-text-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return 'Mail';
      case 'sms':
        return 'MessageSquare';
      case 'whatsapp':
        return 'MessageCircle';
      default:
        return 'Send';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date?.toLocaleDateString();
    }
  };

  return (
    <div className={`bg-surface border border-border rounded-lg p-4 hover:shadow-card transition-micro ${className}`}>
      <div className="flex items-start space-x-3">
        {/* Recipient Avatar */}
        <button
          onClick={() => onContactClick(message?.recipient)}
          className="flex-shrink-0 hover-scale transition-micro"
        >
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center overflow-hidden">
            {message?.recipient?.avatar ? (
              <img 
                src={message?.recipient?.avatar} 
                alt={message?.recipient?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name="User" size={20} className="text-text-secondary" />
            )}
          </div>
        </button>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => onContactClick(message?.recipient)}
                className="font-medium text-text-primary hover:text-primary transition-micro truncate block"
              >
                {message?.recipient?.name}
              </button>
              <div className="flex items-center space-x-2 mt-1">
                <Icon 
                  name={getChannelIcon(message?.channel)} 
                  size={14} 
                  className="text-text-secondary" 
                />
                <span className="text-sm text-text-secondary capitalize">
                  {message?.channel}
                </span>
                <span className="text-sm text-text-secondary">•</span>
                <span className="text-sm text-text-secondary">
                  {message?.type}
                </span>
              </div>
            </div>

            {/* Status and Timestamp */}
            <div className="flex flex-col items-end space-y-1">
              <div className="flex items-center space-x-1">
                <Icon 
                  name={getStatusIcon(message?.status)} 
                  size={16} 
                  className={getStatusColor(message?.status)} 
                />
                <span className={`text-sm font-medium ${getStatusColor(message?.status)}`}>
                  {message?.status}
                </span>
              </div>
              <span className="text-xs text-text-secondary">
                {formatTimestamp(message?.timestamp)}
              </span>
            </div>
          </div>

          {/* Message Preview */}
          <div className="mb-3">
            <p className="text-sm text-text-secondary line-clamp-2">
              {message?.content}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetails(message)}
              iconName="Eye"
              iconPosition="left"
            >
              View Details
            </Button>

            {message?.status === 'failed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRetry(message)}
                iconName="RotateCcw"
                iconPosition="left"
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageCard;